import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { buildPath, getCurrentInstanceSlug } from './lib/tenant';
import { setSEO, setJSONLD, clearJSONLD } from './lib/seo';
import { useQuery } from '@tanstack/react-query';
import {
    Container, Grid, Card, Header, Loader, Dimmer, Image, Button,
    Message, Segment, Select, Label, Icon, Popup,
} from 'semantic-ui-react';
import moment from 'moment';
import { getEventDetail, getOrganiser, getEventAttendees } from './lib/tickets';
import { useAuthStore } from './lib/authStore';
import { globalSettings } from './lib/configuration';
import { t } from './locales';
import { formatMoney, getCurrency } from './lib/currency';
import { withPlatform } from './lib/onlinePlatforms';

const REFERRAL_KEY = 'ticketing_referral';
const API = globalSettings.PORTAL_API_URL;
const PURPLE = '#481b52';

function buildGoogleCalendarUrl({ name, startDt, endDt, description, location, isOnline, platform }) {
    const fmt = (d) => moment(d).utc().format('YYYYMMDDTHHmmss') + 'Z';
    const end = endDt ? fmt(endDt) : fmt(moment(startDt).add(1, 'hour'));
    const p = new URLSearchParams({
        action: 'TEMPLATE',
        text: name,
        dates: `${fmt(startDt)}/${end}`,
        details: description || '',
        location: isOnline ? withPlatform(t('eventDetail.onlineEventLabel'), platform) : (location || ''),
    });
    return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

function AddToCalendar({ event, instance }) {
    if (!instance?.date_time) return null;
    const instanceSlug = getCurrentInstanceSlug();
    if (!instanceSlug) return null;

    const icsUrl = `${API}/api/v1/ticketing/directory/calendar/${instanceSlug}/${event.id}/${instance.id}/calendar.ics`;
    const gcalUrl = buildGoogleCalendarUrl({
        name: event.name,
        startDt: instance.date_time,
        endDt: instance.end_date_time,
        description: event.description,
        location: instance.location || event.location,
        isOnline: event.online_event,
        platform: event.online_platform,
    });

    return (
        <Segment basic style={{ padding: '0.5rem 0', marginTop: '1rem' }}>
            <p style={{ fontWeight: 600, marginBottom: '0.6rem', color: '#444', fontSize: '0.9rem' }}>
                <Icon name="calendar plus outline" /> {t('eventDetail.addToCalendar')}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <a href={icsUrl} download style={{ textDecoration: 'none' }}>
                    <Button size="mini" basic icon labelPosition="left" style={{ color: '#555' }}>
                        <Icon name="download" />
                        {t('eventDetail.icalOutlook')}
                    </Button>
                </a>
                <a href={gcalUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    <Button size="mini" basic icon labelPosition="left" style={{ color: '#4285F4' }}>
                        <Icon name="google" />
                        {t('eventDetail.googleCalendar')}
                    </Button>
                </a>
            </div>
        </Segment>
    );
}

async function fetchRelatedEvents(category) {
    if (!category) return { events: [] };
    const res = await fetch(`${API}/api/v1/ticketing/directory/events/?category=${encodeURIComponent(category)}&sort=date&page_size=4`);
    return res.json();
}

function RelatedEventCard({ event }) {
    const isFree = parseFloat(event.ticket_price) === 0;
    const handleClick = () => {
        const url = event.instance_id
            ? `/i/${event.instance_slug}/events/${event.id}?instance=${event.instance_id}`
            : `/i/${event.instance_slug}/events/${event.id}`;
        window.location.href = url;
    };
    return (
        <Card fluid style={{ borderRadius: 10, cursor: 'pointer', overflow: 'hidden' }} onClick={handleClick}>
            {event.image_url
                ? <Image src={event.image_url} wrapped ui={false} style={{ height: 130, objectFit: 'cover', width: '100%' }} />
                : <div style={{ height: 130, background: `linear-gradient(135deg, ${PURPLE}, #7b3f8a)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '2rem' }}>🎟</span>
                  </div>}
            <Card.Content>
                <Card.Header style={{ fontSize: '0.95rem', color: PURPLE, lineHeight: 1.3 }}>{event.name}</Card.Header>
                {event.next_instance?.date_time && (
                    <Card.Meta style={{ fontSize: '0.8rem' }}>
                        <Icon name="calendar outline" />{moment(event.next_instance.date_time).format('D MMM YYYY')}
                    </Card.Meta>
                )}
            </Card.Content>
            <Card.Content extra>
                <Label size="tiny" style={{ backgroundColor: isFree ? '#27ae60' : PURPLE, color: '#fff' }}>
                    {isFree ? t('eventDetail.free') : formatMoney(event.ticket_price)}
                </Label>
            </Card.Content>
        </Card>
    );
}

const AVATAR_PLACEHOLDER = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><circle cx="20" cy="20" r="20" fill="%23e0d7f5"/><circle cx="20" cy="15" r="8" fill="%23b39ddb"/><ellipse cx="20" cy="36" rx="14" ry="10" fill="%23b39ddb"/></svg>';

function AttendeeList({ data, onLoginClick }) {
    if (!data) return null;

    // Needs auth
    if (data.requires_auth) {
        return (
            <Segment style={{ borderRadius: 12, background: '#f9f0ff', border: '1px solid #e9d8fd', marginTop: '2rem' }}>
                <Header as="h4" style={{ color: 'var(--colour_menu_main)', marginBottom: '0.75rem' }}>
                    <Icon name="users" />
                    {t('eventDetail.whoIsAttending')}
                </Header>
                <p style={{ color: '#555', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                    <Icon name="lock" />
                    {t('eventDetail.loginRequiredBody')}
                </p>
                <Button
                    basic
                    size="small"
                    style={{ color: 'var(--colour_menu_main)' }}
                    onClick={onLoginClick}
                >
                    {t('eventDetail.loginToView')}
                </Button>
            </Segment>
        );
    }

    // Needs a ticket
    if (data.requires_ticket) {
        return (
            <Segment style={{ borderRadius: 12, background: '#f9f0ff', border: '1px solid #e9d8fd', marginTop: '2rem' }}>
                <Header as="h4" style={{ color: 'var(--colour_menu_main)', marginBottom: '0.75rem' }}>
                    <Icon name="users" />
                    {t('eventDetail.whoIsAttending')}
                </Header>
                <p style={{ color: '#555', fontSize: '0.9rem' }}>
                    <Icon name="ticket" />
                    {t('eventDetail.ticketRequiredBody')}
                </p>
            </Segment>
        );
    }

    const { attendees = [], hidden_count = 0, total = 0 } = data;

    if (total === 0) return null;

    return (
        <Segment style={{ borderRadius: 12, marginTop: '2rem', background: '#fafafa' }}>
            <Header as="h4" style={{ color: 'var(--colour_menu_main)', marginBottom: '1rem' }}>
                <Icon name="users" />
                {t('eventDetail.whoIsAttending')}
                <Header.Subheader style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#777' }}>
                    {total === 1
                        ? t('eventDetail.attendingCountOne', { count: total })
                        : t('eventDetail.attendingCountMany', { count: total })}
                </Header.Subheader>
            </Header>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {attendees.map((a, i) => (
                    <AttendeeCard key={i} attendee={a} />
                ))}
            </div>

            {hidden_count > 0 && (
                <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#888' }}>
                    <Icon name="eye slash outline" />
                    {hidden_count === 1
                        ? t('eventDetail.hiddenCountOne', { count: hidden_count })
                        : t('eventDetail.hiddenCountMany', { count: hidden_count })}
                </p>
            )}
        </Segment>
    );
}

function AttendeeCard({ attendee }) {
    const [expanded, setExpanded] = useState(false);
    const hasSocial = attendee.website || attendee.twitter_handle || attendee.linkedin_url;
    const hasDetail = attendee.bio || hasSocial;

    return (
        <div
            style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
                padding: '0.6rem 0.75rem',
                background: '#fff', borderRadius: 10,
                border: '1px solid #ece8f5',
                cursor: hasDetail ? 'pointer' : 'default',
                minWidth: 180, maxWidth: 240, flex: '1 1 180px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                transition: 'box-shadow 0.15s',
            }}
            onClick={() => hasDetail && setExpanded(e => !e)}
            title={hasDetail ? t('eventDetail.clickToSeeMore') : undefined}
        >
            <img
                src={attendee.avatar || AVATAR_PLACEHOLDER}
                alt={attendee.display_name}
                style={{
                    width: 38, height: 38, borderRadius: '50%',
                    objectFit: 'cover', flexShrink: 0,
                    border: '2px solid var(--colour_menu_main)',
                }}
            />
            <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem', color: '#333', lineHeight: 1.3 }}>
                    {attendee.display_name}
                </p>
                {expanded && attendee.bio && (
                    <p style={{ margin: '0.3rem 0 0', fontSize: '0.8rem', color: '#555', lineHeight: 1.4 }}>
                        {attendee.bio.length > 120 ? attendee.bio.slice(0, 120) + '…' : attendee.bio}
                    </p>
                )}
                {expanded && hasSocial && (
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                        {attendee.website && (
                            <a href={attendee.website} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: '0.8rem', color: 'var(--colour_link)' }}>
                                <Icon name="globe" /> {t('eventDetail.web')}
                            </a>
                        )}
                        {attendee.twitter_handle && (
                            <a href={`https://twitter.com/${attendee.twitter_handle}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: '0.8rem', color: 'var(--colour_link)' }}>
                                <Icon name="twitter" /> @{attendee.twitter_handle}
                            </a>
                        )}
                        {attendee.linkedin_url && (
                            <a href={attendee.linkedin_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: '0.8rem', color: 'var(--colour_link)' }}>
                                <Icon name="linkedin" /> {t('eventDetail.linkedin')}
                            </a>
                        )}
                    </div>
                )}
                {hasDetail && !expanded && (
                    <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: '#b39ddb' }}>
                        {t('eventDetail.tapToExpand')}
                    </p>
                )}
            </div>
        </div>
    );
}

function EventDetail() {
    const { eventId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

    const preselectedId = searchParams.get('instanceId') ? parseInt(searchParams.get('instanceId'), 10) : null;
    const [selectedInstanceId, setSelectedInstanceId] = useState(preselectedId);
    const [quantity, setQuantity] = useState(1);
    const [shareCopied, setShareCopied] = useState(false);

    // Capture referral code from ?ref= query param and persist for checkout
    useEffect(() => {
        const ref = searchParams.get('ref');
        if (ref) {
            sessionStorage.setItem(REFERRAL_KEY, ref);
        }
    }, [searchParams]);

    const handleShare = () => {
        const url = window.location.href;
        if (navigator.share) {
            navigator.share({ title: event?.name, url }).catch(() => {});
        } else {
            navigator.clipboard.writeText(url).then(() => {
                setShareCopied(true);
                setTimeout(() => setShareCopied(false), 2000);
            });
        }
    };

    const { data: event, isLoading, isError } = useQuery({
        queryKey: ['ticketing-event', eventId],
        queryFn: () => getEventDetail(eventId),
    });

    const { data: organiserData } = useQuery({
        queryKey: ['ticketing-organiser'],
        queryFn: getOrganiser,
        staleTime: 5 * 60 * 1000,
    });

    // Only fetch attendees if event says the list is visible
    const visibility = event?.attendee_list_visibility ?? 'hidden';
    const shouldFetchAttendees = visibility !== 'hidden';

    const { data: attendeeData } = useQuery({
        queryKey: ['ticketing-event-attendees', eventId],
        queryFn: () => getEventAttendees(eventId),
        enabled: shouldFetchAttendees && !!event && !event?.error,
        retry: false,
    });

    const { data: relatedData } = useQuery({
        queryKey: ['directory-related', event?.category],
        queryFn: () => fetchRelatedEvents(event?.category),
        enabled: !!event && !event?.error && !!event?.category,
        staleTime: 5 * 60 * 1000,
    });
    const relatedEvents = (relatedData?.events ?? []).filter(e => e.id !== parseInt(eventId, 10)).slice(0, 3);

    // SEO — runs whenever event data loads
    useEffect(() => {
        if (!event || event.error) return;
        const nextInstance = event.instances?.find(i => i.date_time) ?? event.instances?.[0];
        setSEO({
            title: `${event.name} — Get Tickets`,
            description: event.description
                ? event.description.slice(0, 160)
                : `Buy tickets for ${event.name}`,
            image: event.image_url || undefined,
            url: window.location.href,
            type: 'event',
        });
        setJSONLD({
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: event.name,
            description: event.description || '',
            ...(event.image_url ? { image: event.image_url } : {}),
            ...(nextInstance?.date_time ? { startDate: nextInstance.date_time } : {}),
            ...(nextInstance?.end_date_time ? { endDate: nextInstance.end_date_time } : {}),
            eventAttendanceMode: event.online_event
                ? 'https://schema.org/OnlineEventAttendanceMode'
                : 'https://schema.org/OfflineEventAttendanceMode',
            eventStatus: 'https://schema.org/EventScheduled',
            location: event.online_event
                ? { '@type': 'VirtualLocation', url: window.location.href }
                : { '@type': 'Place', name: event.location || 'TBC' },
            offers: {
                '@type': 'Offer',
                price: event.display_price ?? event.ticket_price ?? 0,
                priceCurrency: getCurrency(),
                availability: nextInstance?.tickets_remaining === 0
                    ? 'https://schema.org/SoldOut'
                    : 'https://schema.org/InStock',
                url: window.location.href,
            },
            organizer: organiserData?.name
                ? { '@type': 'Organization', name: organiserData.name }
                : undefined,
        });
        return () => clearJSONLD();
    }, [event, organiserData]);

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader size="large" content={t('eventDetail.loading')} />
            </Dimmer>
        );
    }

    if (isError || !event || event.error) {
        return (
            <Container className="contentContainer">
                <Message negative>
                    <Message.Header>{t('eventDetail.notFoundTitle')}</Message.Header>
                    <p>{t('eventDetail.notFoundBody')}</p>
                </Message>
                <Button onClick={() => navigate(buildPath('/events'))}>{t('eventDetail.backToEvents')}</Button>
            </Container>
        );
    }

    const instances = event.instances ?? [];
    const mustChoose = instances.length > 1 && selectedInstanceId === null;
    const selectedInstance = instances.find(i => i.id === selectedInstanceId) ?? (instances.length === 1 ? instances[0] : null);

    const instanceOptions = instances.map(i => ({
        key: i.id,
        value: i.id,
        text: `${moment(i.date_time).format('ddd D MMM YYYY, HH:mm')}${i.location ? ` — ${i.location}` : ''}`,
        disabled: i.tickets_remaining !== null && i.tickets_remaining === 0,
    }));

    const quantityOptions = Array.from({ length: Math.min(10, selectedInstance?.tickets_remaining ?? 10) }, (_, i) => ({
        key: i + 1,
        value: i + 1,
        text: t('eventDetail.ticketCount', { count: i + 1, plural: i + 1 > 1 ? 's' : '' }),
    }));

    // Each date carries its own price (what create-order will charge for it); before a date is
    // chosen, the series' price is shown
    const priced = selectedInstance?.display_price != null ? selectedInstance : event;
    const displayPrice = priced.display_price ?? priced.ticket_price;

    const ticketsRemaining = selectedInstance?.tickets_remaining ?? null;
    const isSoldOut = selectedInstance !== null && ticketsRemaining === 0;
    const isLowStock = ticketsRemaining !== null && ticketsRemaining > 0 && ticketsRemaining <= 10;

    const stockColor = ticketsRemaining <= 3 ? '#c0392b' : ticketsRemaining <= 10 ? '#e67e22' : '#555';

    const handleGetTickets = () => {
        const instanceId = selectedInstance?.id;
        if (!instanceId) return;

        const checkoutParams = `?instanceId=${instanceId}&qty=${quantity}&eventId=${eventId}`;
        if (!isAuthenticated) {
            navigate(buildPath(`/register?next=${encodeURIComponent(buildPath('/checkout') + checkoutParams)}`));
        } else {
            navigate(buildPath(`/checkout${checkoutParams}`));
        }
    };

    return (
        <Container className="contentContainer">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <Button
                    basic
                    icon="arrow left"
                    content={t('eventDetail.allEvents')}
                    onClick={() => navigate(buildPath('/events'))}
                />
                <Popup
                    content={shareCopied ? t('eventDetail.linkCopied') : t('eventDetail.copyLink')}
                    trigger={
                        <Button
                            basic
                            icon="share alternate"
                            content={t('eventDetail.share')}
                            onClick={handleShare}
                        />
                    }
                    open={shareCopied || undefined}
                    position="left center"
                />
            </div>

            <Grid stackable>
                <Grid.Column width={10}>
                    {event.image_url && (
                        <Image
                            src={event.image_url}
                            fluid
                            rounded
                            style={{ marginBottom: '1.5rem', maxHeight: 400, objectFit: 'cover' }}
                        />
                    )}
                    {!event.image_url && (
                        <div style={{
                            height: 280,
                            background: 'linear-gradient(135deg, var(--colour_menu_main), var(--colour_menu_light))',
                            borderRadius: 12,
                            marginBottom: '1.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '5rem' }}>🎟</span>
                        </div>
                    )}

                    <Header as="h1" className="sectionHeader">{event.name}</Header>

                    {event.location && (
                        <p style={{ color: '#666', marginBottom: '0.5rem' }}>
                            <Icon name="map marker alternate" />
                            {event.location}
                        </p>
                    )}

                    <p style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap', marginTop: '1rem' }}>
                        {event.description}
                    </p>

                    {/* About the organiser */}
                    {organiserData && (
                        <Segment style={{ borderRadius: 12, marginTop: '2rem', background: '#f9f9f9' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                {organiserData.logo && (
                                    <Image
                                        src={organiserData.logo}
                                        style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 8 }}
                                    />
                                )}
                                <div>
                                    <p style={{ margin: 0, fontWeight: 700, color: 'var(--colour_menu_main)' }}>
                                        {organiserData.name}
                                    </p>
                                    {organiserData.description && (
                                        <p style={{ margin: '0.2rem 0 0', fontSize: '0.9rem', color: '#555' }}>
                                            {organiserData.description.length > 120
                                                ? organiserData.description.slice(0, 120) + '…'
                                                : organiserData.description}
                                        </p>
                                    )}
                                    {organiserData.contact_email && (
                                        <a
                                            href={`mailto:${organiserData.contact_email}`}
                                            style={{ fontSize: '0.85rem', color: 'var(--colour_link)' }}
                                        >
                                            {organiserData.contact_email}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </Segment>
                    )}
                    {/* Attendee list */}
                    {visibility !== 'hidden' && (
                        <AttendeeList
                            data={attendeeData}
                            onLoginClick={() => navigate(buildPath(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`))}
                        />
                    )}
                </Grid.Column>

                <Grid.Column width={6}>
                    <Segment raised style={{ borderRadius: 12, padding: '2rem' }}>
                        <Header as="h3" style={{ color: 'var(--colour_menu_main)', marginBottom: '1.5rem' }}>
                            {t('eventDetail.getTickets')}
                        </Header>

                        <Label
                            size="large"
                            style={{
                                backgroundColor: 'var(--colour_heading_success)',
                                color: '#fff',
                                marginBottom: '1.5rem',
                                display: 'inline-block',
                            }}
                        >
                            {t('eventDetail.pricePerTicket', { price: formatMoney(displayPrice) })}
                        </Label>
                        {priced.ticket_price_non_member != null && (
                            <p style={{ margin: '-1rem 0 1.5rem', fontSize: '0.85rem', color: '#777' }}>
                                {priced.viewer_is_member
                                    ? t('eventDetail.memberPriceApplied', { price: formatMoney(priced.ticket_price_non_member) })
                                    : t('eventDetail.nonMemberPriceNote', { price: formatMoney(priced.ticket_price) })}
                            </p>
                        )}

                        {instances.length > 1 && (
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                                    {t('eventDetail.selectDate')}
                                </label>
                                <Select
                                    fluid
                                    placeholder={t('eventDetail.chooseADate')}
                                    options={instanceOptions}
                                    value={selectedInstance?.id ?? null}
                                    onChange={(_, { value }) => { setSelectedInstanceId(value); setQuantity(1); }}
                                />
                                {/* A hint, not an error: it used to show in red with a red outline as
                                    soon as the page opened, before the buyer had done anything */}
                                {mustChoose && (
                                    <p style={{ color: '#767676', fontSize: '0.85rem', marginTop: '0.35rem' }}>
                                        {t('eventDetail.pleaseChooseDate')}
                                    </p>
                                )}
                            </div>
                        )}

                        {selectedInstance && (
                            <Segment basic style={{ padding: '0.5rem 0', marginBottom: '1rem' }}>
                                <p style={{ margin: 0 }}>
                                    <Icon name="calendar alternate outline" />
                                    <strong>{moment(selectedInstance.date_time).format('dddd D MMMM YYYY')}</strong>
                                </p>
                                <p style={{ margin: '0.25rem 0 0' }}>
                                    <Icon name="clock outline" />
                                    {moment(selectedInstance.date_time).format('HH:mm')}
                                    {selectedInstance.end_date_time && ` – ${moment(selectedInstance.end_date_time).format('HH:mm')}`}
                                </p>
                                {selectedInstance.location && (
                                    <p style={{ margin: '0.25rem 0 0' }}>
                                        <Icon name="map marker alternate" />
                                        {selectedInstance.location}
                                    </p>
                                )}
                                {ticketsRemaining !== null && (
                                    <p style={{ margin: '0.5rem 0 0', color: stockColor, fontWeight: isLowStock ? 600 : 400 }}>
                                        <Icon name="ticket" />
                                        {isSoldOut
                                            ? t('eventDetail.soldOut')
                                            : isLowStock
                                                ? t('eventDetail.onlyXLeft', { count: ticketsRemaining, plural: ticketsRemaining !== 1 ? 's' : '' })
                                                : t('eventDetail.ticketsRemaining', { count: ticketsRemaining })}
                                    </p>
                                )}
                            </Segment>
                        )}

                        {!isSoldOut && selectedInstance && (
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                                    {t('eventDetail.quantity')}
                                </label>
                                <Select
                                    fluid
                                    options={quantityOptions}
                                    value={quantity}
                                    onChange={(_, { value }) => setQuantity(value)}
                                />
                            </div>
                        )}

                        {!isSoldOut && selectedInstance && (
                            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f9f9f9', borderRadius: 8 }}>
                                <span style={{ fontWeight: 600 }}>{t('eventDetail.total')} </span>
                                <span style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--colour_menu_main)' }}>
                                    {formatMoney(parseFloat(displayPrice) * quantity)}
                                </span>
                            </div>
                        )}

                        <Button
                            fluid
                            size="large"
                            disabled={mustChoose || isSoldOut || !selectedInstance}
                            onClick={handleGetTickets}
                            style={{
                                backgroundColor: isSoldOut ? '#ccc' : 'var(--colour_menu_main)',
                                color: '#fff',
                                borderRadius: 8,
                            }}
                        >
                            {isSoldOut ? t('eventDetail.soldOutButton') : t('eventDetail.continueToCheckout')}
                        </Button>

                        {/* Sold out — contact organiser */}
                        {isSoldOut && organiserData?.contact_email && (
                            <div style={{
                                marginTop: '1rem', padding: '0.75rem',
                                background: '#fff8e1', borderRadius: 8,
                                border: '1px solid #ffe082',
                            }}>
                                <p style={{ margin: 0, fontSize: '0.9rem', color: '#555' }}>
                                    <Icon name="envelope outline" />
                                    {t('eventDetail.waitlistPrompt')}
                                </p>
                                <a
                                    href={`mailto:${organiserData.contact_email}?subject=Waitlist - ${event.name}`}
                                    style={{ color: 'var(--colour_menu_main)', fontWeight: 600 }}
                                >
                                    {organiserData.contact_email}
                                </a>
                            </div>
                        )}

                        {!isAuthenticated && !isSoldOut && selectedInstance && (
                            <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#888', textAlign: 'center' }}>
                                {t('eventDetail.signInPrompt')}
                            </p>
                        )}

                        <AddToCalendar event={event} instance={selectedInstance ?? instances[0]} />
                    </Segment>
                </Grid.Column>
            </Grid>

            {relatedEvents.length > 0 && (
                <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #eee' }}>
                    <Header as="h3" style={{ color: PURPLE, marginBottom: '1.25rem' }}>
                        <Icon name="calendar outline" />
                        {t('eventDetail.moreEventsLikeThis')}
                    </Header>
                    <Grid stackable columns={3}>
                        {relatedEvents.map(e => (
                            <Grid.Column key={e.id}>
                                <RelatedEventCard event={e} />
                            </Grid.Column>
                        ))}
                    </Grid>
                </div>
            )}
        </Container>
    );
}

export default EventDetail;
